import React, { useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import AdminNav from "@/components/AdminNav";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

type Submission = {
  id: string;
  answer: string;
  ip: string;
  ua?: string;
  createdAt: number;
};

export default function Queue() {
  const { user } = useAuth();
  const [subs, setSubs] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/queue");
      if (!res.ok) {
        setSubs([]);
        return;
      }
      const data = await res.json();
      setSubs(data.submissions ?? []);
    } catch (e) {
      setSubs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueue();
  }, []);

  const doAction = async (id: string, action: "approve" | "decline") => {
    setActionLoading((s) => ({ ...s, [id]: true }));
    try {
      const res = await fetch("/api/queue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, action }),
      });
      if (res.ok) {
        setSubs((s) => s.filter((x) => x.id !== id));
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data?.message ?? "Action failed");
      }
    } catch (e) {
      alert("Action failed");
    } finally {
      setActionLoading((s) => ({ ...s, [id]: false }));
    }
  };

  return (
    <>
      <AdminNav />
      <div className="min-h-screen p-8 bg-background">
        <div className="max-w-6xl mx-auto mt-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold">Moderation Queue</h1>
              <p className="text-sm text-muted-foreground">Only authenticated users can moderate submissions.</p>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Pending submissions</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? <div>Loading...</div> : null}
              {!loading && subs.length === 0 ? (
                <div className="text-sm text-muted-foreground">No submissions in queue.</div>
              ) : null}
              <div className="space-y-4">
                {subs.map((s) => (
                  <div key={s.id} className="p-4 border rounded">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="text-lg font-medium">{s.answer}</div>
                        <div className="text-sm text-muted-foreground">{new Date(s.createdAt).toLocaleString()}</div>
                        <div className="text-sm text-muted-foreground">IP: {s.ip} • UA: {s.ua}</div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="secondary" size="sm" onClick={() => doAction(s.id, "approve")} disabled={!!actionLoading[s.id]}>
                          Approve
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => doAction(s.id, "decline")} disabled={!!actionLoading[s.id]}>
                          Decline
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
